import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

import { get as _get } from 'lodash';

import { Hierarchify } from '../../../../d3/utils';
import { GetISODateFromQuick } from '../../../../libraries/helpers';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import MetadataService from '../../../../data/metadata-service';
import DashboardService from '../../../../data/dashboard-service';
import Auth, { ParamsSerializer } from '../../../../data/auth-service';

import FilterBuilder from '../../../../components/filter/filterbuilder';
import AgentsHierarchy from '../../../../components/charts/agentshierarchy';
import DateRange from './../components/date-range';
import { VerifyDates } from '../../widget';

const useStyles = makeStyles(theme => ({
    root: {
    },
    // editor classes
    editorRoot: {
        position: 'relative',
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    tagsButton: {
        width: 99,
    },
    filterText: {
        marginLeft: 10,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    chip: {
        maxWidth: 200,
        /* elipsis at beginning of string */
        '& span': {
            direction: 'rtl',
            textAlign: 'left',
        },
    },
    addEmailBtn: {
        marginTop: 25,
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 15 }
    : { minW: 3, minH: 10 };

// don't display until size-me can figure out what our size is
const UserAgentsWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
UserAgentsWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const Widget = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [, setErrors] = useState(0);
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const mdPerms = Auth.permissions.get('Metadata');

    const fetch = controller => {
        props.setBusy(true);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        if (mdPerms.read) {
            return DashboardService.getUserAgents({
                start,
                end,
                filter: props.data.settings.json_filter,
                group: props.data.settings.group,
                order: props.data.settings.order,
                search: props.data.settings.search,
                agg: props.data.settings.agg,
                limit: props.data.settings.limit,
                xua: props.data.settings.xua,
            }).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            }, controller);
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);
        const aggs = _get(res, 'aggregations.user_agents.buckets', []);
        const h = Hierarchify(aggs, props.data.settings.agg === 'activity'
            ? d => d.doc_count
            : d => d.originators.value);
        setData(h);
    };

    const subscribe = () => {
        setErrors(0);
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const handleOnRemove = d => {
        if (_get(props, 'data.settings.xua', []).indexOf(d.data.ua) < 0) {
            props.data.settings.xua = [...(props.data.settings.xua || []), d.data.ua];
            props.onUpdate(props.data);
        }
    };

    const handleOnLink = d => {
        const p = {
            start,
            end,
            quick: props.data.quick,
            sources: ['http'],
        };

        // currently bro_log.user_agent.text searches aren't working - try plain text search instead.
        if (_get(props.data, 'settings.group') !== 'full') {
            p.search = d.data.ua;
            p.filter = JSON.stringify(props.data.settings.json_filter);
        } else {
            const variable = 'bro_log.user_agent'; //  + (_get(props.data, 'settings.group') === 'full' ? '' : '.txt');
            const f = {
                operator: 'And',
                nodes: [{ variable, value: `${d.data.ua}` }],
            };
            if (_get(props.data, 'settings.json_filter.nodes', []).length > 0) {
                f.nodes.push(props.data.settings.json_filter);
            }
            p.filter = JSON.stringify(f);
        }

        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    };

    useEffect(() => {
        setErrors(0);
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    useEffect(() => {
        subscribe();
    }, [props.data.start, props.data.end, props.data.quick]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            <AgentsHierarchy
                data={data}
                height={props.size.height}
                width={props.size.width}
                scale={_get(props.data, 'settings.data.scale')}
                shape={_get(props.data, 'settings.data.shape')}
                speed={_get(props.data, 'settings.data.speed')}
                onRemove={handleOnRemove}
                onLink={handleOnLink}/>
        </div>
    );
};
Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
    onUpdate: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const daForm = useValidate({});

    const [fields, setFields] = useState([]);
    const [title, setTitle] = useState(props.data.title);
    const [filter, setFilter] = useState(props.data.settings.json_filter);
    const [order, setOrder] = useState(props.data.settings.order || 'Most');
    const [agg, setAgg] = useState(props.data.settings.agg || 'activity');
    const [xua, setXua] = useState(props.data.settings.xua || []);
    const [xuaValue, setXuaValue] = useState('');
    const [search, setSearch] = useState(props.data.settings.search || '');
    const [group, setGroup] = useState(props.data.settings.group || 'full');
    const [limit, setLimit] = useState(props.data.settings.limit || 42);
    const [shape, setShape] = useState(_get(props, 'data.settings.data.shape', 'Circle'));
    const [scale, setScale] = useState(_get(props, 'data.settings.data.scale', 'linear'));
    const [speed, setSpeed] = useState(_get(props, 'data.settings.data.speed', 'Fast'));
    const [showFilter, setShowFilter] = useState(false);
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const mdPerms = Auth.permissions.get('Metadata');

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleOrderChange = e => setOrder(e.target.value);

    const handleGroupChange = e => setGroup(e.target.value);

    const handleAggChange = e => setAgg(e.target.value);

    const handleSearchChange = e => setSearch(e.target.value);

    const handleShapeChange = e => setShape(e.target.value);

    const handleScaleChange = e => setScale(e.target.value);

    const handleSpeedChange = e => setSpeed(e.target.value);

    const handleXUAChange = e => setXuaValue(e.target.value);

    const handleLimitChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setLimit(+e.target.value);
    };

    const handleClearSearch = () => setSearch('');

    const handleFilterChange = ({ filter }) => {
        setFilter(filter);
        setShowFilter(false);
    };

    const handleAddXUA = () => {
        if (!(xua || []).find(e => e === xuaValue)) {
            setXua([...(xua || []), xuaValue]);
        }
        setXuaValue('');
    };
    const handleRemoveXUA = x => {
        const i = (xua || []).findIndex(e => e === x);
        if (i >= 0) {
            xua.splice(i, 1);
            setXua([...xua]);
        }
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;
        const formState = daForm.get() || {};
        if (Object.keys(formState).length === 0 && title && title.trim().length > 0) {
            return true;
        }
        return checkValidity(formState);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                json_filter: filter,
                order,
                agg,
                search,
                limit,
                group,
                xua,
                data: { shape, speed, scale },
            },
        });
    };

    const fetchFields = async () => {
        if (mdPerms.read) {
            const fields = await MetadataService.cacheFields();
            setFields(fields.filter(c => c.indexed && c.type !== 'timestamp'));
        }
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box style={{ position: 'absolute', width: props.size.width - 2, height: props.size.height - 40, overflow: 'auto' }}>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                </div>
                {props.data.date_override &&
                    <div className={classes.inlineForm}>
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    </div>
                }
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <FilterBuilder
                                onOK={handleFilterChange}
                                onShow={setShowFilter}
                                show={showFilter}
                                fields={fields}
                                filter={filter}
                                placement="right-start"
                                button={
                                    <Button
                                        className={classes.tagsButton}
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AccountTreeIcon/>}>Filter
                                    </Button>
                                }/>
                            <Typography
                                variant="body2"
                                component="span"
                                className={classes.filterText}>
                                {ReactHtmlParser(MetadataService.toEnglish(filter, true))}
                            </Typography>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.inlineForm}>
                    <TextField
                        select
                        label="Order"
                        aria-label="Order"
                        value={order}
                        onChange={handleOrderChange}>
                        {['Most', 'Least'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Granularity"
                        aria-label="Granularity"
                        value={group}
                        onChange={handleGroupChange}>
                        {[
                            { key: 'full', title: 'Full User Agent' },
                            { key: 'app', title: 'Application Name' },
                        ].map(({ key, title }) => (
                            <MenuItem key={key} value={key}>
                                {title}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Aggregation"
                        aria-label="Aggregation"
                        value={agg}
                        onChange={handleAggChange}>
                        {[
                            { key: 'activity', title: 'Activity' },
                            { key: 'orig', title: 'Originator' }
                        ].map(({ key, title }) => (
                            <MenuItem key={key} value={key}>
                                {title}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Validator
                        style={{ width: 75 }}
                        valueType="range"
                        type="number"
                        label="Max"
                        aria-label="Max Results"
                        value={limit}
                        inputProps={{ min: 1, max: 111 }}
                        helperText="Max Results 1 - 111"
                        onChange={handleLimitChange}>
                    </Validator>
                    <TextField
                        style={{ width: 300 }}
                        label="Agent Filter"
                        aria-label="Agent Filter"
                        helperText="'iphone or ipad', 'proxy'"
                        value={search}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment:
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>,
                            endAdornment:
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear DNS filter"
                                        onClick={handleClearSearch}
                                        edge="end"
                                        disabled={!search}>
                                        <ClearIcon fontSize="inherit"/>
                                    </IconButton>
                                </InputAdornment>
                        }}/>
                </div>
                <div className={classes.inlineForm}>
                    <TextField
                        select
                        label="Shape"
                        aria-label="Shape"
                        value={shape}
                        onChange={handleShapeChange}>
                        {['Circle', 'Rectangle'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Scale"
                        aria-label="Scale"
                        value={scale}
                        onChange={handleScaleChange}>
                        {[
                            { key: 'linear', title: 'Linear' },
                            { key: 'log', title: 'Logarithmic' },
                        ].map(({ key, title }) => (
                            <MenuItem key={key} value={key}>
                                {title}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Animation"
                        aria-label="Animation"
                        value={speed}
                        onChange={handleSpeedChange}>
                        {['Fast', 'Slow', 'None'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.inlineForm}>
                    <TextField
                        label="Exclude User Agents"
                        aria-label="Exclude User Agents"
                        helperText="Enter User Agents to exclude from graph"
                        value={xuaValue}
                        unique={xua}
                        onChange={handleXUAChange}/>
                    <Button
                        aria-label="Add user agent filter"
                        onClick={handleAddXUA}
                        color="secondary"
                        variant="contained"
                        size="small"
                        className={classes.addEmailBtn}
                        disabled={!xuaValue}>
                        Exclude
                    </Button>
                    <div style={{ maxWidth: props.size.width - 100 }}>
                        {(xua || []).map(e =>
                            <div key={e} className={classes.inlineChips}>
                                <Chip
                                    style={{ paddingRight: 5 }}
                                    key={e}
                                    size="small"
                                    color='secondary'
                                    label={e}
                                    aria-label={e}
                                    className={classes.chip}
                                    onDelete={() => handleRemoveXUA(e)}/>
                            </div>
                        )}
                    </div>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button
                        aria-label="cancel"
                        onClick={props.onCancel}
                        color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    UserAgentsWidget as default,
    DEFAULTS,
};
